var React = require('react');
var { useRef, useState } = React;

var DEFAULT_GROUP_COLOR = '#64b4ff';
var GROUP_COLOR_OPTIONS = [
  '#4f67b6', '#77a6cc', '#92a93c', '#739f88',
  '#b65d32', '#6a936d', '#c4cf63', '#7b90c9',
  '#b1bddf', '#d79a63', '#aaa596', '#8f7c49',
  '#c4959b', '#ad6f71', '#d9a07f'
];

function normalizeGroupColor(color) {
  var value = String(color || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : DEFAULT_GROUP_COLOR;
}

function hexToRgba(hex, alpha) {
  var value = normalizeGroupColor(hex).slice(1);
  var r = parseInt(value.slice(0, 2), 16);
  var g = parseInt(value.slice(2, 4), 16);
  var b = parseInt(value.slice(4, 6), 16);
  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
}

function useGroupManagement(opts) {
  var setWorkflowState = opts.setWorkflowState;
  var workflowStateRef = opts.workflowStateRef;
  var getRect = opts.getRect;
  var sizes = opts.sizes;
  var showAlert = opts.showAlert;
  var setSelectedNodeIds = opts.setSelectedNodeIds;
  var selectedNodeIdsRef = opts.selectedNodeIdsRef;

  var [collapsingAnim, setCollapsingAnim] = useState(null);
  var [expandingAnim, setExpandingAnim] = useState(null);
  var [repellingNodeIds, setRepellingNodeIds] = useState(null);
  var [groupColorMenuId, setGroupColorMenuId] = useState(null);
  var collapsingTimerRef = useRef(null);
  var expandingTimerRef = useRef(null);
  var repelTimerRef = useRef(null);
  var preRepelPositionsRef = useRef({});
  var groupEditState = useState(null);
  var editingGroupId = groupEditState[0];
  var setEditingGroupId = groupEditState[1];

  function areNodesConnected(ids, conns) {
    var idSet = {};
    for (var i = 0; i < ids.length; i++) idSet[ids[i]] = true;
    var adj = {};
    for (var j = 0; j < ids.length; j++) adj[ids[j]] = [];
    for (var k = 0; k < conns.length; k++) {
      var c = conns[k];
      if (idSet[c.from] && idSet[c.to]) {
        adj[c.from].push(c.to);
        adj[c.to].push(c.from);
      }
    }
    var visited = {};
    var queue = [ids[0]];
    visited[ids[0]] = true;
    while (queue.length > 0) {
      var curr = queue.shift();
      var neighbors = adj[curr] || [];
      for (var n = 0; n < neighbors.length; n++) {
        if (!visited[neighbors[n]]) { visited[neighbors[n]] = true; queue.push(neighbors[n]); }
      }
    }
    for (var v = 0; v < ids.length; v++) {
      if (!visited[ids[v]]) return false;
    }
    return true;
  }

  function getGroupCardRect(group) {
    var gPos = group && group.position ? group.position : { left: 0, top: 0 };
    var measured = sizes[group.id];
    var w = measured ? measured.width : 250;
    var ht = measured ? measured.height : 80;
    return { left: gPos.left, top: gPos.top, right: gPos.left + w, bottom: gPos.top + ht, width: w, height: ht };
  }

  function getExpandedGroupBounds(groupNodeIds, allNodes) {
    var pad = 28;
    var groupNodeSet = {};
    var rects = [];
    groupNodeIds.forEach(function(nid) { groupNodeSet[nid] = true; });
    for (var i = 0; i < allNodes.length; i++) {
      var node = allNodes[i];
      if (!groupNodeSet[node.id]) continue;
      var rect = getRect(node);
      var hasMeasured = !!sizes[node.id];
      if (!hasMeasured) {
        var estW = 380, estH = 140;
        if (node.icon === 'agent') estH = 160;
        if (node.icon === 'classifier' || node.icon === 'router') { estW = 400; estH = 200; }
        rect.width = estW; rect.height = estH; rect.right = rect.left + estW; rect.bottom = rect.top + estH;
      } else { rect.bottom += 16; rect.height += 16; }
      rects.push(rect);
    }
    if (!rects.length) return null;
    var left = rects[0].left, top = rects[0].top, right = rects[0].right, bottom = rects[0].bottom;
    for (var ri = 1; ri < rects.length; ri++) {
      if (rects[ri].left < left) left = rects[ri].left;
      if (rects[ri].top < top) top = rects[ri].top;
      if (rects[ri].right > right) right = rects[ri].right;
      if (rects[ri].bottom > bottom) bottom = rects[ri].bottom;
    }
    return { left: left - pad, top: top - pad, right: right + pad, bottom: bottom + pad, width: (right - left) + pad * 2, height: (bottom - top) + pad * 2 };
  }

  function getGroupExpandLayoutShift(group, allNodes) {
    if (!group || !group.position) return { dx: 0, dy: 0 };
    var bounds = getExpandedGroupBounds(group.nodeIds || [], allNodes || []);
    if (!bounds) return { dx: 0, dy: 0 };
    var targetCx = group.position.left + 140;
    var targetCy = group.position.top + 40;
    var currentCx = (bounds.left + bounds.right) / 2;
    var currentCy = (bounds.top + bounds.bottom) / 2;
    return { dx: Math.round(targetCx - currentCx), dy: Math.round(targetCy - currentCy) };
  }

  function computeRepelDisplacements(groupNodeIds, allNodes, allGroups, nodeIdToGroupIdMap, sourceGroup) {
    var gap = 24;
    var groupNodeSet = {};
    groupNodeIds.forEach(function(nid) { groupNodeSet[nid] = true; });
    var items = [];
    for (var i = 0; i < allNodes.length; i++) {
      var node = allNodes[i];
      if (groupNodeSet[node.id]) continue;
      if (nodeIdToGroupIdMap[node.id]) continue;
      items.push({ id: node.id, rect: getRect(node), isGroupCard: false });
    }
    if (allGroups) {
      allGroups.forEach(function(g) {
        if (groupNodeSet[g.id] || g.expanded) return;
        var isRelated = false;
        for (var k = 0; k < g.nodeIds.length; k++) { if (groupNodeSet[g.nodeIds[k]]) { isRelated = true; break; } }
        if (isRelated) return;
        items.push({ id: '__group__' + g.id, rect: getGroupCardRect(g), isGroupCard: true });
      });
    }
    var obstacles = [];
    for (var gi = 0; gi < allNodes.length; gi++) {
      if (!groupNodeSet[allNodes[gi].id]) continue;
      var gNode = allNodes[gi];
      var gr = getRect(gNode);
      var hasMeasured = !!sizes[gNode.id];
      if (!hasMeasured) {
        var estW = 380, estH = 140;
        if (gNode.icon === 'agent') estH = 160;
        if (gNode.icon === 'classifier' || gNode.icon === 'router') { estW = 400; estH = 200; }
        gr.width = estW; gr.height = estH; gr.right = gr.left + estW; gr.bottom = gr.top + estH;
      } else { gr.bottom += 16; gr.height += 16; }
      obstacles.push(gr);
    }
    if (obstacles.length === 0) return {};

    function rectsOverlap(a, b) {
      return a.right + gap > b.left && a.left - gap < b.right && a.bottom + gap > b.top && a.top - gap < b.bottom;
    }
    function minPushToClear(tr, obs) {
      var opts = [
        { dx: obs.right + gap - tr.left, dy: 0 },
        { dx: -(tr.right + gap - obs.left), dy: 0 },
        { dx: 0, dy: obs.bottom + gap - tr.top },
        { dx: 0, dy: -(tr.bottom + gap - obs.top) }
      ];
      opts.forEach(function(o) { o.cost = Math.abs(o.dx) + Math.abs(o.dy); });
      opts.sort(function(a, b) { return a.cost - b.cost; });
      return opts[0];
    }
    function offsetRect(r, dx, dy) {
      return { left: r.left + dx, top: r.top + dy, right: r.right + dx, bottom: r.bottom + dy, width: r.width, height: r.height };
    }

    var displacements = {};
    for (var j = 0; j < items.length; j++) {
      var item = items[j];
      var r = item.rect;
      var totalDx = 0, totalDy = 0;
      for (var iter = 0; iter < 5; iter++) {
        var pushed = false;
        for (var oi = 0; oi < obstacles.length; oi++) {
          if (!rectsOverlap(r, obstacles[oi])) continue;
          var push = minPushToClear(r, obstacles[oi]);
          totalDx += push.dx; totalDy += push.dy;
          r = offsetRect(r, push.dx, push.dy);
          pushed = true;
        }
        if (!pushed) break;
      }
      if (totalDx !== 0 || totalDy !== 0) {
        displacements[item.id] = { dx: Math.round(totalDx), dy: Math.round(totalDy) };
        item.rect = r;
      }
    }
    // Domino cascade
    for (var pass = 0; pass < 8; pass++) {
      var changed = false;
      for (var a = 0; a < items.length; a++) {
        if (!displacements[items[a].id]) continue;
        for (var b = 0; b < items.length; b++) {
          if (a === b) continue;
          if (!rectsOverlap(items[a].rect, items[b].rect)) continue;
          var push2 = minPushToClear(items[b].rect, items[a].rect);
          var prev = displacements[items[b].id] || { dx: 0, dy: 0 };
          displacements[items[b].id] = { dx: Math.round(prev.dx + push2.dx), dy: Math.round(prev.dy + push2.dy) };
          items[b].rect = offsetRect(items[b].rect, push2.dx, push2.dy);
          changed = true;
        }
      }
      if (!changed) break;
    }
    return displacements;
  }

  function groupSelectedNodes(event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    var ids = Object.keys(selectedNodeIdsRef.current);
    if (ids.length < 2) return;
    if (collapsingAnim) return;
    var conns = workflowStateRef.current.connections || [];
    if (!areNodesConnected(ids, conns)) {
      showAlert('Cannot group: all selected nodes must be connected to each other.');
      return;
    }
    var sumLeft = 0, sumTop = 0, count = 0;
    var currentNodes = workflowStateRef.current.nodes || [];
    var iconColorMap = {
      start: '#7ee8c8', agent: '#7f96b8', data: '#60b8f4', validation: '#a8b8ff',
      sync: '#68d9b0', condition: '#f5c842', variables: '#6f8fb2', 'set-variable': '#6f8fb2',
      'console-log': '#b890e8', classifier: '#f09060', router: '#e89b5e', sleep: '#80b8f0', loop: '#60d8c0',
      'file-preview': '#88b8f8', custom: '#d7dce4'
    };
    var groupIcons = [];
    for (var ni = 0; ni < currentNodes.length; ni++) {
      if (ids.indexOf(currentNodes[ni].id) !== -1 && currentNodes[ni].position) {
        var nRect = getRect(currentNodes[ni]);
        sumLeft += nRect.left + nRect.width / 2;
        sumTop += nRect.top + nRect.height / 2;
        count++;
        if (groupIcons.length < 4) {
          groupIcons.push({ type: currentNodes[ni].icon || 'custom', color: iconColorMap[currentNodes[ni].icon] || '#6f8fb2' });
        }
      }
    }
    var centerLeft = count > 0 ? Math.round(sumLeft / count) - 140 : 0;
    var centerTop = count > 0 ? Math.round(sumTop / count) - 40 : 0;
    var centerX = centerLeft + 140;
    var centerY = centerTop + 40;

    setCollapsingAnim({ nodeIds: ids, centerX: centerX, centerY: centerY });
    setSelectedNodeIds({});

    if (collapsingTimerRef.current) clearTimeout(collapsingTimerRef.current);
    collapsingTimerRef.current = setTimeout(function() {
      collapsingTimerRef.current = null;
      setCollapsingAnim(null);
      var groupId = 'group-' + Date.now().toString(36);
      setWorkflowState(function(prev) {
        var groups = Array.isArray(prev.groups) ? prev.groups.slice() : [];
        groups = groups.map(function(g) {
          var filtered = g.nodeIds.filter(function(nid) { return ids.indexOf(nid) === -1; });
          if (filtered.length === g.nodeIds.length) return g;
          if (filtered.length < 2) return null;
          return Object.assign({}, g, { nodeIds: filtered });
        }).filter(Boolean);
        groups.push({
          id: groupId, name: 'Group ' + (groups.length + 1), nodeIds: ids, collapsed: true,
          position: { left: centerLeft, top: centerTop }, icons: groupIcons, color: DEFAULT_GROUP_COLOR
        });
        return Object.assign({}, prev, { groups: groups });
      });
    }, 450);
  }

  function ungroupNodes(groupId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (expandingAnim) return;
    var groups = Array.isArray(workflowStateRef.current.groups) ? workflowStateRef.current.groups : [];
    var group = null;
    for (var i = 0; i < groups.length; i++) { if (groups[i].id === groupId) { group = groups[i]; break; } }
    if (!group) return;

    var fromLeft = group.position ? group.position.left + 140 : 0;
    var fromTop = group.position ? group.position.top + 40 : 0;

    var currentNodes = workflowStateRef.current.nodes || [];
    var currentGroups = workflowStateRef.current.groups || [];
    var currentNodeIdToGroupId = {};
    currentGroups.forEach(function(g) {
      if (g.id === groupId) return;
      if (g.expanded) return;
      g.nodeIds.forEach(function(nid) { currentNodeIdToGroupId[nid] = g.id; });
    });
    var displacements = computeRepelDisplacements(group.nodeIds, currentNodes, currentGroups, currentNodeIdToGroupId, group);
    var displacedIds = Object.keys(displacements);

    if (displacedIds.length > 0) setRepellingNodeIds(displacedIds);

    setWorkflowState(function(prev) {
      var prevGroups = Array.isArray(prev.groups) ? prev.groups : [];
      var updatedNodes = prev.nodes;
      var updatedGroups = prevGroups.filter(function(g) { return g.id !== groupId; });
      if (displacedIds.length > 0) {
        updatedNodes = (prev.nodes || []).map(function(n) {
          if (!displacements[n.id]) return n;
          var d = displacements[n.id];
          var pos = n.position || { left: 0, top: 0 };
          return Object.assign({}, n, { position: { left: pos.left + d.dx, top: pos.top + d.dy } });
        });
        updatedGroups = updatedGroups.map(function(g) {
          var gKey = '__group__' + g.id;
          if (!displacements[gKey]) return g;
          var d = displacements[gKey];
          var pos = g.position || { left: 0, top: 0 };
          return Object.assign({}, g, { position: { left: pos.left + d.dx, top: pos.top + d.dy } });
        });
      }
      return Object.assign({}, prev, { nodes: updatedNodes, groups: updatedGroups });
    });
    setSelectedNodeIds({});

    setExpandingAnim({ nodeIds: group.nodeIds, fromX: fromLeft, fromY: fromTop });
    if (expandingTimerRef.current) clearTimeout(expandingTimerRef.current);
    expandingTimerRef.current = setTimeout(function() { expandingTimerRef.current = null; setExpandingAnim(null); }, 450);
    if (displacedIds.length > 0) {
      if (repelTimerRef.current) clearTimeout(repelTimerRef.current);
      repelTimerRef.current = setTimeout(function() { repelTimerRef.current = null; setRepellingNodeIds(null); }, 550);
    }
  }

  function deleteGroup(groupId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setWorkflowState(function(prev) {
      var groups = Array.isArray(prev.groups) ? prev.groups : [];
      var group = groups.find(function(g) { return g.id === groupId; });
      if (!group) return prev;
      var idSet = {};
      group.nodeIds.forEach(function(nid) { idSet[nid] = true; });
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).filter(function(n) { return !idSet[n.id]; }),
        connections: (prev.connections || []).filter(function(c) { return !idSet[c.from] && !idSet[c.to]; }),
        groups: groups.filter(function(g) { return g.id !== groupId; })
      });
    });
    setSelectedNodeIds({});
  }

  function toggleExpandGroup(groupId) {
    if (expandingAnim || collapsingAnim) return;
    var groups = Array.isArray(workflowStateRef.current.groups) ? workflowStateRef.current.groups : [];
    var group = null;
    for (var i = 0; i < groups.length; i++) { if (groups[i].id === groupId) { group = groups[i]; break; } }
    if (!group) return;

    var fromLeft = group.position ? group.position.left + 140 : 0;
    var fromTop = group.position ? group.position.top + 40 : 0;

    if (group.expanded) {
      setCollapsingAnim({ nodeIds: group.nodeIds, centerX: fromLeft, centerY: fromTop });
      var savedPositions = preRepelPositionsRef.current[groupId];
      if (savedPositions) {
        var restoreIds = Object.keys(savedPositions);
        if (restoreIds.length > 0) {
          setRepellingNodeIds(restoreIds);
          setWorkflowState(function(prev) {
            var restoredNodes = (prev.nodes || []).map(function(n) {
              if (!savedPositions[n.id]) return n;
              return Object.assign({}, n, { position: Object.assign({}, savedPositions[n.id]) });
            });
            var restoredGroups = (prev.groups || []).map(function(g) {
              var gKey = '__group__' + g.id;
              if (!savedPositions[gKey]) return g;
              return Object.assign({}, g, { position: Object.assign({}, savedPositions[gKey]) });
            });
            return Object.assign({}, prev, { nodes: restoredNodes, groups: restoredGroups });
          });
          if (repelTimerRef.current) clearTimeout(repelTimerRef.current);
          repelTimerRef.current = setTimeout(function() { repelTimerRef.current = null; setRepellingNodeIds(null); }, 500);
        }
        delete preRepelPositionsRef.current[groupId];
      }
      if (collapsingTimerRef.current) clearTimeout(collapsingTimerRef.current);
      collapsingTimerRef.current = setTimeout(function() {
        collapsingTimerRef.current = null;
        setCollapsingAnim(null);
        setWorkflowState(function(prev) {
          var gs = (prev.groups || []).map(function(g) {
            if (g.id !== groupId) return g;
            return Object.assign({}, g, { expanded: false });
          });
          return Object.assign({}, prev, { groups: gs });
        });
      }, 450);
    } else {
      var currentNodes = workflowStateRef.current.nodes || [];
      var currentGroups = workflowStateRef.current.groups || [];
      var groupLayoutShift = getGroupExpandLayoutShift(group, currentNodes);
      var shiftedNodes = currentNodes.map(function(n) {
        if (group.nodeIds.indexOf(n.id) === -1) return n;
        var pos = n.position || { left: 0, top: 0 };
        return Object.assign({}, n, { position: { left: pos.left + groupLayoutShift.dx, top: pos.top + groupLayoutShift.dy } });
      });
      var currentNodeIdToGroupId = {};
      currentGroups.forEach(function(g) {
        if (g.expanded || g.id === groupId) return;
        g.nodeIds.forEach(function(nid) { currentNodeIdToGroupId[nid] = g.id; });
      });
      var displacements = computeRepelDisplacements(group.nodeIds, shiftedNodes, currentGroups, currentNodeIdToGroupId, group);
      var displacedIds = Object.keys(displacements);
      if (displacedIds.length > 0) {
        var saved = {};
        displacedIds.forEach(function(id) {
          if (id.indexOf('__group__') === 0) {
            var gid = id.replace('__group__', '');
            var grp = currentGroups.find(function(g) { return g.id === gid; });
            if (grp && grp.position) saved[id] = { left: grp.position.left, top: grp.position.top };
          } else {
            var nd = currentNodes.find(function(n) { return n.id === id; });
            if (nd && nd.position) saved[id] = { left: nd.position.left, top: nd.position.top };
          }
        });
        preRepelPositionsRef.current[groupId] = saved;
        setRepellingNodeIds(displacedIds);
      }
      setWorkflowState(function(prev) {
        var gs = (prev.groups || []).map(function(g) {
          if (g.id !== groupId) return g;
          return Object.assign({}, g, { expanded: true });
        });
        var updatedNodes = (prev.nodes || []).map(function(n) {
          if (group.nodeIds.indexOf(n.id) === -1) return n;
          var pos = n.position || { left: 0, top: 0 };
          return Object.assign({}, n, { position: { left: pos.left + groupLayoutShift.dx, top: pos.top + groupLayoutShift.dy } });
        });
        var updatedGroups = gs;
        if (displacedIds.length > 0) {
          updatedNodes = updatedNodes.map(function(n) {
            if (!displacements[n.id]) return n;
            var d = displacements[n.id]; var pos = n.position || { left: 0, top: 0 };
            return Object.assign({}, n, { position: { left: pos.left + d.dx, top: pos.top + d.dy } });
          });
          updatedGroups = updatedGroups.map(function(g) {
            var gKey = '__group__' + g.id;
            if (!displacements[gKey]) return g;
            var d = displacements[gKey]; var pos = g.position || { left: 0, top: 0 };
            return Object.assign({}, g, { position: { left: pos.left + d.dx, top: pos.top + d.dy } });
          });
        }
        return Object.assign({}, prev, { nodes: updatedNodes, groups: updatedGroups });
      });
      setExpandingAnim({ nodeIds: group.nodeIds, fromX: fromLeft, fromY: fromTop });
      if (expandingTimerRef.current) clearTimeout(expandingTimerRef.current);
      expandingTimerRef.current = setTimeout(function() { expandingTimerRef.current = null; setExpandingAnim(null); }, 450);
      if (displacedIds.length > 0) {
        if (repelTimerRef.current) clearTimeout(repelTimerRef.current);
        repelTimerRef.current = setTimeout(function() { repelTimerRef.current = null; setRepellingNodeIds(null); }, 550);
      }
    }
  }

  function collapseAllExpandedGroups() {
    var groups = workflowStateRef.current.groups || [];
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].expanded) toggleExpandGroup(groups[i].id);
    }
  }

  function renameGroup(groupId, newName) {
    setWorkflowState(function(prev) {
      var groups = (prev.groups || []).map(function(g) {
        if (g.id !== groupId) return g;
        return Object.assign({}, g, { name: newName });
      });
      return Object.assign({}, prev, { groups: groups });
    });
  }

  function setGroupColor(groupId, color) {
    var nextColor = normalizeGroupColor(color);
    setWorkflowState(function(prev) {
      var groups = (prev.groups || []).map(function(g) {
        if (g.id !== groupId) return g;
        return Object.assign({}, g, { color: nextColor });
      });
      return Object.assign({}, prev, { groups: groups });
    });
  }

  return {
    DEFAULT_GROUP_COLOR: DEFAULT_GROUP_COLOR,
    GROUP_COLOR_OPTIONS: GROUP_COLOR_OPTIONS,
    normalizeGroupColor: normalizeGroupColor,
    hexToRgba: hexToRgba,
    collapsingAnim: collapsingAnim,
    expandingAnim: expandingAnim,
    repellingNodeIds: repellingNodeIds,
    groupColorMenuId: groupColorMenuId,
    setGroupColorMenuId: setGroupColorMenuId,
    editingGroupId: editingGroupId,
    setEditingGroupId: setEditingGroupId,
    groupSelectedNodes: groupSelectedNodes,
    ungroupNodes: ungroupNodes,
    deleteGroup: deleteGroup,
    toggleExpandGroup: toggleExpandGroup,
    collapseAllExpandedGroups: collapseAllExpandedGroups,
    renameGroup: renameGroup,
    setGroupColor: setGroupColor,
    getGroupCardRect: getGroupCardRect
  };
}

module.exports = useGroupManagement;
